import React, { useEffect, useState } from 'react';
import { Draggable, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';

import styles from './DraggableItem.module.scss';
import { MountData } from './DroppableArea';

type DraggableItemProps = {
  draggableId: string;
  draggableText: string;
  index: number;
  cannotDrag?: boolean;
  isDraggingOver?: boolean;
  className?: string;
  correct?: boolean;
  incorrect?: boolean;
  disabled?: boolean;
  minHeight?: number;
  onMount?: (data: MountData) => void;
};

function DraggableItem({
  draggableId,
  draggableText,
  index,
  cannotDrag,
  isDraggingOver,
  className = '',
  correct,
  incorrect,
  disabled,
  minHeight,
  onMount,
}: DraggableItemProps) {
  const [mountData, setMountData] = useState<MountData>();

  useEffect(() => {
    if (mountData && onMount) onMount(mountData);
  }, [mountData]); // eslint-disable-line

  function handleRef(el: HTMLDivElement) {
    if (mountData || !el?.clientHeight) return;
    setMountData({ height: el?.clientHeight });
  }

  function getClassName(snapshot?: DraggableStateSnapshot): string {
    return [
      styles.item,
      styles.itemWithArrow,
      className,
      snapshot?.isDragging ? styles.itemIsDragging : '',
      isDraggingOver ? styles.itemIsDraggingOver : '',
      correct ? styles.itemIsCorrect : '',
      incorrect ? styles.itemIsIncorrect : '',
    ].join(' ');
  }

  return (
    // ATENTION: keep the key, otherwise the swap will not work
    <div
      className={styles.itemWrapper}
      key={draggableId}
      ref={handleRef}
      style={{ minHeight }}
    >
      <Draggable
        draggableId={draggableId}
        index={index}
        isDragDisabled={disabled}
      >
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={getClassName(snapshot)}
            style={{
              ...provided.draggableProps.style,
              // invisible, because needs to be rendered to avoid errors
              display: cannotDrag ? 'none' : undefined,
            }}
          >
            {!disabled && <FontAwesomeIcon icon={faGripVertical} />}
            <div style={{ flex: 1, textAlign: 'center' }}>{draggableText}</div>
          </div>
        )}
      </Draggable>
      {/* visible element when cannot drag... is exactly the same above, without Draggable wrapper */}
      {cannotDrag && <div className={getClassName()}>{draggableText}</div>}
    </div>
  );
}

export default DraggableItem;
